/**
 * ensureAliasesAgree — write-time alias PREVENTION (never migration).
 *
 * Purpose: stop the write path from emitting new divergent alias pairs.
 * This does NOT walk existing records or backfill history — that's a
 * CTO-owned migration (`Specifications/CTO_MIGRATION_SPEC_ALIAS_SYNC.md`)
 * that emits the reconciliation triple `R-3` a client-side write-through
 * cannot.
 *
 * Rules (operator ruling 2026-08-03 §1 · revised for launch safety):
 *
 *   1. Both sides agree → no-op (agreement isn't a defect).
 *
 *   2. Exactly one side present (the other is null/undefined) → mirror
 *      the present value into the missing side. This is the mechanical
 *      class — DEC-61 canonical direction is snake→camel on null-camel,
 *      and camel→snake on null-snake.
 *
 *   3. Both sides present AND different → RECORD AND PASS THROUGH.
 *      - Do NOT mirror (would silently pick a winner, violating DEC-61).
 *      - Do NOT throw (would break saves on the 40 divergent-customerId
 *        rows already in the tenant — precisely the records under
 *        active cleanup — punishing operators for a data defect they
 *        can't fix; operator ruling §1a/§1c 2026-08-03).
 *      - Emit a structured conflict record via the optional onConflict
 *        callback so the class can be measured through telemetry rather
 *        than through user-facing failures.
 *      - Return the payload unchanged for that pair. The save proceeds
 *        exactly as it does today.
 *
 * Rationale for rule 3 (operator §1b):
 *   The upside of throwing is that conflicts surface loudly. We already
 *   know the conflicts. We do not need the operator's save to tell us.
 *   Throwing costs broken saves and buys information we already hold.
 *
 *   B-4 ("a reverse read with no filter must fail loudly") is NOT relaxed
 *   by this rule — B-4 governs reads that could silently disclose the
 *   wrong data. Rule 3 governs writes on records already divergent, where
 *   failing loud blocks the operator and protects nothing.
 *
 * Alias classes handled:
 *   - Money: lineTotal / line_total (P0)
 *   - Money: totalAmount / total_amount
 *   - Identity: customerId / customer_id (DEF-121 territory;
 *       conflicts recorded with adjudication classification)
 *   - Denormalized: customerName / customer_name (derives from customerId)
 *   - Attribute: leadSource / lead_source
 *   - Branding: secondaryColor / secondary_color, textColor / text_color,
 *       headerColor / header_color (P1 per BR-1 blast-radius bounding)
 *
 * ─── Measured populations ──────────────────────────────────────────────────
 * IDI-8 (2026-08-06): this header previously carried figures — "601 pairs",
 * "436 null-canonical rows", "40 on customerId", "32 divergent" — attributed to
 * commit `6085f82`. A full paginated scan of all 494,656 items in
 * bidit-dev-quotes could not reproduce ANY of them:
 *
 *              header claimed   actually measured
 *   lineTotal pairs      436          80  (61 null-sided, since backfilled to 0)
 *   customerId divergent  40           4  (all 4 on the operator-signed list)
 *   totalAmount diverg.   32           2
 *
 * The stated numbers were treated downstream as a measured population and used
 * to size remediation work; they are not reproducible and should not be relied
 * on. Do NOT re-add counts to this comment. Counts drift the moment a migration
 * or a save runs, and a stale number in a source comment is indistinguishable
 * from a fresh one. Measure on demand instead:
 *   bidit/migration/scripts/linetotal-nullfill.py   (dry-run reports the triple)
 *   bidit/scripts/def161-report.mjs                 (read-only CO population)
 *
 * DEF-149 · Nevado · 2026-08-03
 * Populations corrected · IDI-8 · 2026-08-06
 */

// Alias pairs handled at the top-level (quote header).
// Left = canonical (camelCase per DEC-61); right = snake alias.
const HEADER_PAIRS = [
  ['totalAmount', 'total_amount'],
  ['customerId', 'customer_id'],
  ['customerName', 'customer_name'],
  ['leadSource', 'lead_source'],
  ['secondaryColor', 'secondary_color'],
  ['textColor', 'text_color'],
  ['headerColor', 'header_color'],
];

// Alias pairs handled per line item.
const LINE_ITEM_PAIRS = [
  ['lineTotal', 'line_total'],
];

// Aliases where a value-side conflict must be classified for adjudication
// (DEF-121 bucket) rather than treated as a routine class-alias record.
const ADJUDICATION_REQUIRED = new Set(['customerId']);

function isMissing(v) {
  return v === null || v === undefined;
}

/**
 * Reconcile a single alias pair on a plain object.
 * MUTATES the object on rule-2 mirror; leaves untouched on rule-3 record.
 * Calls onConflict on rule-3.
 */
function reconcilePair(obj, camelKey, snakeKey, path, onConflict) {
  if (!obj || typeof obj !== 'object') return;

  const camelPresent = camelKey in obj;
  const snakePresent = snakeKey in obj;
  if (!camelPresent && !snakePresent) return;

  const camelVal = obj[camelKey];
  const snakeVal = obj[snakeKey];
  const camelMissing = !camelPresent || isMissing(camelVal);
  const snakeMissing = !snakePresent || isMissing(snakeVal);

  // Rule 1: both agree (including both missing) — no-op.
  if (!camelMissing && !snakeMissing && camelVal === snakeVal) return;
  if (camelMissing && snakeMissing) return;

  // Rule 2: exactly one side present — mirror it.
  if (camelMissing && !snakeMissing) {
    obj[camelKey] = snakeVal;
    return;
  }
  if (!camelMissing && snakeMissing) {
    obj[snakeKey] = camelVal;
    return;
  }

  // Rule 3: both sides present AND different — record and pass through.
  //   - Do NOT mirror (would silently pick a winner).
  //   - Do NOT throw (would break saves on records under active cleanup).
  //   - Emit a structured record for telemetry / DEF-121 routing.
  //   - Payload is unchanged for this pair.
  if (typeof onConflict === 'function') {
    const classification = ADJUDICATION_REQUIRED.has(camelKey)
      ? 'ALIAS_CONFLICT_ADJUDICATION' // DEF-121 territory
      : 'ALIAS_CONFLICT';
    try {
      onConflict({
        classification,
        pair: `${camelKey}/${snakeKey}`,
        path: path || '(root)',
        camel: camelVal,
        snake: snakeVal,
      });
    } catch (_e) {
      // Defensive: telemetry MUST NOT block the save. If onConflict
      // throws for any reason, swallow — the save is the user's job,
      // the record is ours.
    }
  }
}

/**
 * Ensure alias pairs agree on the outbound quote payload.
 *
 * @param {object} payload — the quote save body, mutated in place for rule 2.
 * @param {object} [options]
 * @param {(record: {classification, pair, path, camel, snake}) => void} [options.onConflict]
 *   Called ONCE per rule-3 divergence with a structured record. Never
 *   propagated to the caller — telemetry that throws is swallowed.
 * @returns {object} the same payload; for rule 3 the pair is unchanged.
 */
export function ensureAliasesAgree(payload, options) {
  if (!payload || typeof payload !== 'object') return payload;

  const onConflict = options && options.onConflict;

  // Header pairs.
  for (const [camel, snake] of HEADER_PAIRS) {
    reconcilePair(payload, camel, snake, camel, onConflict);
  }

  // Line-item pairs (support both spellings of the array key too).
  const linesArr = payload.line_items ?? payload.lineItems;
  if (Array.isArray(linesArr)) {
    linesArr.forEach((li, idx) => {
      if (!li || typeof li !== 'object') return;
      for (const [camel, snake] of LINE_ITEM_PAIRS) {
        reconcilePair(li, camel, snake, `line_items[${idx}].${camel}`, onConflict);
      }
    });
  }

  return payload;
}

// Named exports for testing.
export const _internals = {
  HEADER_PAIRS,
  LINE_ITEM_PAIRS,
  ADJUDICATION_REQUIRED,
  reconcilePair,
  isMissing,
};
